// src/components/shell/popovers/HelpPopover.tsx
// Sidebar '?' that opens a popover with the active mode's help content. Replaces
// the inline blue info-box mount. Auto-opens whenever the user enters a mode
// whose help content version is newer than the user's last dismissal. The
// "Don't show this again" button stamps the current version into localStorage
// per useHelpDismissal.
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { SidebarRailButton } from "@/components/shell/Sidebar/SidebarRailButton";
import {
  getHelpEntry,
  type HelpEntryCtx,
} from "@/components/shell/help/modeHelpRegistry";
import { useHelpDismissal } from "@/hooks/useHelpDismissal";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { railPopoverPanelStyle, useRailPopover } from "./useRailPopover";

export type HelpPopoverProps = {
  expanded: boolean;
  mode: PrimaryMode;
  salesperson: { name: string } | null;
  location: { name: string } | null;
  onAfterAction?: () => void;
};

export function HelpPopover({
  expanded,
  mode,
  salesperson,
  location,
  onAfterAction,
}: HelpPopoverProps) {
  const { appDisplayName } = useOrgSettings();
  const { open, setOpen, toggle, triggerRef, panelRef, position } = useRailPopover();
  const { shouldAutoOpen, dismiss, dismissForSession } = useHelpDismissal(mode);
  const lastAutoOpenedMode = useRef<PrimaryMode | null>(null);

  // Auto-open when entering a mode whose content version exceeds the cached
  // dismissed version. Re-arms each time the mode changes so the popover can
  // pop again for a different mode in the same session. Close if hydrate later
  // shows this mode was already dismissed.
  useEffect(() => {
    if (lastAutoOpenedMode.current === mode) {
      if (!shouldAutoOpen) setOpen(false);
      return;
    }
    lastAutoOpenedMode.current = mode;
    setOpen(shouldAutoOpen);
  }, [mode, shouldAutoOpen, setOpen]);

  const ctx: HelpEntryCtx = {
    salesperson,
    location,
    appDisplayName,
  };
  const entry = getHelpEntry(mode, ctx);

  return (
    <>
      <SidebarRailButton
        ref={triggerRef}
        expanded={expanded}
        glyph="?"
        label="Help"
        active={open}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
      />
      {open
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Help"
              className="rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-zinc-950 dark:text-gray-100"
              style={railPopoverPanelStyle(position)}
            >
              <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Help
                  </div>
                  <div className="mt-0.5 truncate text-sm font-semibold">{entry.title}</div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    dismissForSession();
                    setOpen(false);
                  }}
                  aria-label="Close help"
                  className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  ✕
                </button>
              </div>
              <div className="px-4 py-3 text-sm leading-relaxed text-gray-800 dark:text-gray-100">
                {entry.body}
              </div>
              <div className="border-t border-gray-100 px-4 py-2 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    dismiss();
                    setOpen(false);
                    onAfterAction?.();
                  }}
                  className="w-full rounded-lg px-3 py-2 text-left text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800/70"
                >
                  Don't show this again
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
